import {ChangeDetectorRef, Component, Inject, OnInit, Renderer2} from "@angular/core";
import {NgClass} from "@angular/common";
import { environment } from '../../../../../environments/environment';
import {WindowRefService} from "../../../../services/window-ref.service";
import {EventBusService} from "../../../../services/event-bus.service";
import {HotelToursSearchService} from "../../../../services/search/hotel-tours-search.service";
import {
  HotelToursCalendarParams,
  HotelToursCalendarResponse,
  HotelToursCalendarRow,
} from "../../../../types/interfaces/hotel-tour-search";
import {OnDestroy$} from "@pdtec/ngx-observable-lifecycle";
import {Subject} from "rxjs";
import {Swiper} from 'swiper';
import {FreeMode, Scrollbar} from 'swiper/modules';
import {PriceFormatPipe} from "../../../pipes/price-format/price-format.pipe";
import {HotelPriceCalendarSkeletonComponent} from "./calendar-skeleton/calendar-skeleton.component";
import {HotelTourCalendarDatesService} from "../../../../services/hotel-tour-calendar/dates.service";
import {takeUntil} from "rxjs/operators";
import {MODE} from "../../../../models";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../../../config/hotel-tours-list-page.config";
import {HotelToursSearchParams} from "../../../../types/interfaces/hotel-tour-search";

Swiper.use([FreeMode, Scrollbar]);

@Component({
  selector: 'app-hotel-price-calendar',
  templateUrl: environment.mobile ? './calendar.component.mobile.html' : './calendar.component.html',
  styleUrls: environment.mobile ? ['./calendar.component.mobile.scss'] : ['./calendar.component.scss'],
  imports: [PriceFormatPipe, HotelPriceCalendarSkeletonComponent, NgClass],
  standalone: true
})
export class HotelPriceCalendarComponent extends OnDestroy$ implements OnInit {
  private destroy$ = new Subject<void>();
  private resizeObserver: ResizeObserver;
  private skeletonObserver: IntersectionObserver;
  private isLoadingMoreMobile = false;
  private isMobileEdgeLoadingEnabled = false;
  private pendingMobileSlideDirection: 'prev' | 'next' | null = null;
  private pendingMobileAnchorIndex: number | null = null;
  private pendingLinerSelectionScroll = false;
  private pendingAppliedDateScroll: string | null = null;
  private selectedLinerCell: { date: string; nights: number } | null = null;
  private mobileLoadingArrowElement: Element | null = null;
  isShowSkeleton: boolean = true;
  calendarRows: any[] = [];
  dates: string[];
  dateFrom: string;
  dateTo: string;
  startDiapason: string;
  endDiapason: string;
  nightsFrom: number;
  nightsTo: number;
  datesGrid: { day: string; weekday: string; month: string; date: string }[] = [];
  swiper: Swiper;
  bodyParams: { params: HotelToursCalendarParams };
  activeCells: { nights: number; date: string }[] = [];
  reloadCalendar = false;
  canScrollToPast = true;
  isLoad = false;
  private renderDelayTimeout: any;

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
    private cdRef: ChangeDetectorRef,
    private windowService: WindowRefService,
    private searchService: HotelToursSearchService,
    private eventBusService: EventBusService,
    private hotelTourCalendarDatesService: HotelTourCalendarDatesService,
    private renderer: Renderer2
  ) {
    super();
  }

  ngOnInit() {
    this.eventBusService.changeModalTitle$.next(HotelPriceCalendarComponent.calendarModalTitle());
    this.updateParams();
    this.checkCanScrollToPast();
    this.prepareAppliedDateScroll();

    this.eventBusService.priceCalendarLinerSelection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selection) => {
        this.applyPriceCalendarLinerSelection(selection);
      });

    this.eventBusService.priceCalendarAppliedSelection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selection) => {
        this.applyPriceCalendarAppliedSelection(selection);
      });

    this.getHotelPriceCalendar();

    this.eventBusService.refreshShortSearchCalendar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshCalendar();
      });

    this.eventBusService.applyMobilePriceCalendarSelection$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncActiveCellWithShortSearchParams();
      });
  }

  ngOnDestroy() {
    if (this.renderDelayTimeout) {
      clearTimeout(this.renderDelayTimeout);
      this.renderDelayTimeout = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.skeletonObserver) {
      this.skeletonObserver.disconnect();
      this.skeletonObserver = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateParams() {
    this.bodyParams = this.searchService.getHotelToursCalendarParams();
    this.dateFrom = this.bodyParams.params.dateFrom;
    this.dateTo = this.bodyParams.params.dateTo;
    this.startDiapason = this.dateFrom;
    this.endDiapason = this.dateTo;

    this.bodyParams.params.hotelsIds = [this.hotelToursPageConfig.hotel.hotelId];

    if (!environment.mobile) {
      this.ensureFixedDateRange(11);
    }
  }

  private ensureFixedDateRange(daysCount: number) {
    const start = new Date(this.startDiapason);
    const newEnd = new Date(start);
    newEnd.setDate(start.getDate() + (daysCount - 1));

    this.dateTo = this.hotelTourCalendarDatesService.getFormattedDateString(newEnd);
    this.endDiapason = this.dateTo;
    this.bodyParams.params.dateTo = this.dateTo;
  }

  trackByFn(item) {
    return item;
  }

  private static calendarModalTitle() {
    return '<div class="tourists-modal__title">Цены на туры</div>';
  }

  initSwiper() {
    if (!environment.mobile) {
      return;
    }

    const self = this;
    this.swiper = new Swiper('#swiperCalendar', {
      freeMode: true,
      allowTouchMove: true,
      slidesPerView: 'auto',
      initialSlide: this.canScrollToPast ? 1 : 0,
      scrollbar: {
        el: '.swiper-scrollbar',
        draggable: true, // позволяет пользователю перетаскивать скролл
        hide: false, // чтобы скроллбар всегда был видимым
      },
      on: {
        touchStart: () => {
          this.isMobileEdgeLoadingEnabled = true;
        },
        sliderMove(swiper) {
          if (!self.isMobileEdgeLoadingEnabled) {
            return;
          }

          const startSlide = swiper.slides[0];
          const startSlideRect = startSlide.getBoundingClientRect();
          const swiperRect = swiper.el.getBoundingClientRect();
          const slideWidth = startSlideRect.right - startSlideRect.left;
          const isHalfVisible = startSlideRect.right > swiperRect.left + slideWidth * 0.8;

          if (isHalfVisible && self.canScrollToPast && self.isLoad && !self.isLoadingMoreMobile) {
            self.isLoad = false;
            self.load('prev');
          }

          if (swiper.activeIndex >= swiper.slides.length - 6) {
            const endSlide = swiper.slides[swiper.slides.length - 1];
            const endSlideRect = endSlide.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const endSlideWidth = endSlideRect.right - endSlideRect.left;
            const isEndHalfVisible =
              endSlideRect.left < viewportWidth &&
              endSlideRect.right - endSlideWidth * 0.8 <= viewportWidth;

            if (isEndHalfVisible && self.isLoad && !self.isLoadingMoreMobile) {
              self.isLoad = false;
              self.load('next');
            }
          }
        },
        slideChange: (swiper) => {
          if (!this.isMobileEdgeLoadingEnabled) {
            return;
          }

          if (swiper.activeIndex >= swiper.slides.length - 2 && this.isLoad && !this.isLoadingMoreMobile) {
            this.isLoad = false;
            this.load('next');
          }

          if (swiper.activeIndex <= 1 && this.canScrollToPast && this.isLoad && !this.isLoadingMoreMobile) {
            this.isLoad = false;
            this.load('prev');
          }
        }
      },
    });

    // Ensure correct initial widths after render/layout
    setTimeout(() => {
      this.swiper?.update();
      if (environment.mobile && this.swiper) {
        if (this.pendingMobileAnchorIndex !== null) {
          this.swiper.slideTo(this.pendingMobileAnchorIndex, 0);
          this.pendingMobileAnchorIndex = null;
          this.pendingMobileSlideDirection = null;
        } else {
          this.scrollToPendingSelection();
        }
        setTimeout(() => {
          this.finishMobileEdgeLoad();
        }, 0);
      }
    }, 0);

    this.initSkeletonObserver();
    this.initResizeObserver();
  }

  private getHotelPriceCalendar(inStart?: boolean) {
    const latestSnapshot = this.searchService.getLatestPriceCalendarSnapshot();

    if (inStart === undefined && latestSnapshot) {
      this.updateCalendarRangeFromResponse(latestSnapshot);
      this.applyCalendarResponse(latestSnapshot);
      return;
    }

    this.searchService.getCachedHotelPriceCalendar(this.bodyParams, { updateLatest: inStart === undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (inStart !== undefined) {
            this.searchService.publishHotelPriceCalendarResponse(this.bodyParams, response);
          }

          const calendarResponse = this.searchService.getLatestPriceCalendarSnapshot() || response;

          this.updateCalendarRangeFromResponse(calendarResponse);

          this.applyCalendarResponse(calendarResponse);

          if (inStart !== undefined) {
            this.eventBusService.priceCalendarModalRangeLoaded$.next({
              dateFrom: this.bodyParams.params.dateFrom,
              dateTo: this.bodyParams.params.dateTo,
            });
          }
        },
        error: (error) => {
          this.finishMobileEdgeLoad();
          console.error('Ошибка при запросе календаря:', error);
        }
      });
  }

  private applyCalendarResponse(response: HotelToursCalendarResponse) {
    if (response.calendar.length > 0) {
      this.calendarRows = response.calendar.map((row) => ({
        ...row,
        prices: [...row.prices],
      }));
      this.reloadCalendar = false;

      this.setDatesGrid();
      this.isLoad = true;

      if (this.renderDelayTimeout) {
        clearTimeout(this.renderDelayTimeout);
      }
      this.renderDelayTimeout = setTimeout(() => {
        this.isShowSkeleton = false;
        this.cdRef.detectChanges();
        if (!this.swiper && environment.mobile) {
          this.initSwiper();
        } else {
          setTimeout(() => {
            this.swiper?.update();
            if (environment.mobile) {
              if (this.pendingMobileAnchorIndex !== null && this.swiper) {
                this.swiper.slideTo(this.pendingMobileAnchorIndex, 0);
              } else if (this.pendingMobileSlideDirection) {
                this.swiperSlide(this.pendingMobileSlideDirection === 'prev');
              } else {
                this.scrollToPendingSelection();
              }
              this.pendingMobileAnchorIndex = null;
              this.pendingMobileSlideDirection = null;
              setTimeout(() => {
                this.finishMobileEdgeLoad();
              }, 0);
              this.initSkeletonObserver();
            }
          }, 0);
        }
      }, 150);

      return;
    }

    this.calendarRows = [];
    this.datesGrid = [];
    this.isShowSkeleton = false;
    this.finishMobileEdgeLoad();
    this.cdRef.detectChanges();
  }

  private updateCalendarRangeFromResponse(response: HotelToursCalendarResponse) {
    const dates: string[] = [];

    response.calendar.forEach((row) => {
      row.prices.forEach((price) => {
        if (price.date) {
          dates.push(price.date);
        }
      });
    });

    this.searchService.getLatestPriceCalendarRangesSnapshot().forEach((range) => {
      if (range.dateFrom) {
        dates.push(range.dateFrom);
      }

      if (range.dateTo) {
        dates.push(range.dateTo);
      }
    });

    if (!dates.length) {
      return;
    }

    dates.sort();
    this.startDiapason = dates[0];
    this.endDiapason = dates[dates.length - 1];
    this.dateFrom = this.startDiapason;
    this.dateTo = this.endDiapason;
  }

  private setDatesGrid() {
    this.datesGrid = this.hotelTourCalendarDatesService.getDatesGrid(this.startDiapason, this.endDiapason);
    this.checkCanScrollToPast();
  }

  private refreshCalendar() {
    if (this.renderDelayTimeout) {
      clearTimeout(this.renderDelayTimeout);
      this.renderDelayTimeout = null;
    }

    this.reloadCalendar = true;
    this.isShowSkeleton = true;
    this.isLoad = false;
    this.isLoadingMoreMobile = false;
    this.isMobileEdgeLoadingEnabled = false;
    this.pendingMobileAnchorIndex = null;
    this.pendingMobileSlideDirection = null;
    this.pendingLinerSelectionScroll = false;
    this.pendingAppliedDateScroll = null;
    this.selectedLinerCell = null;
    this.mobileLoadingArrowElement = null;
    this.activeCells = [];
    this.calendarRows = [];
    this.datesGrid = [];

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.skeletonObserver) {
      this.skeletonObserver.disconnect();
      this.skeletonObserver = null;
    }

    this.updateParams();
    this.checkCanScrollToPast();
    this.cdRef.detectChanges();
    this.getHotelPriceCalendar();
  }

  private initSkeletonObserver() {
    const swiperEl = this.swiper?.el;
    if (!swiperEl) return;

    if (this.skeletonObserver) {
      this.skeletonObserver.disconnect();
      this.skeletonObserver = null;
    }

    const skeletonSlides = swiperEl.querySelectorAll('[data-skeleton]');
    if (!skeletonSlides.length) return;

    this.skeletonObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (!this.isMobileEdgeLoadingEnabled) return;

        const type = entry.target.getAttribute('data-skeleton');

        // Подгрузка вперёд
        if (type === 'next' && this.isLoad && !this.isLoadingMoreMobile) {
          this.isLoad = false;
          this.load('next');
        }

        // Подгрузка назад
        if (type === 'prev' && this.canScrollToPast && this.isLoad && !this.isLoadingMoreMobile) {
          this.isLoad = false;
          this.load('prev');
        }
      });
    }, {
      root: swiperEl,
      threshold: 0.15
    });

    skeletonSlides.forEach(slide => this.skeletonObserver?.observe(slide));
  }

  private initResizeObserver() {
    if (this.resizeObserver || !this.swiper?.el) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.swiper?.update();
    });

    this.resizeObserver.observe(this.swiper.el);
  }


  getFilteredPrices(row: HotelToursCalendarRow, date: string) {
    return row.prices.filter(price => price.date === date);
  }

  getPriceForDate(row: HotelToursCalendarRow, date: string) {
    return row.prices.find(price => price.date === date) ?? null;
  }

  get datesDiapason() {
    const dates = this.hotelTourCalendarDatesService.getDatesDiapason(this.startDiapason, this.endDiapason);

    return `${dates.dateFrom} - ${dates.dateTo}`;
  }

  slideToStart() {
    this.swiper?.slideTo(0, 500);
    if (environment.mobile && this.canScrollToPast && this.isLoad && !this.isLoadingMoreMobile) {
      this.isMobileEdgeLoadingEnabled = true;
      this.load('prev');
    }
  }

  slideToEnd() {
    if (!this.swiper) {
      return;
    }
    this.swiper.slideTo(Math.max(this.swiper.slides.length - 1, 0), 500);
    if (environment.mobile && this.isLoad && !this.isLoadingMoreMobile) {
      this.isMobileEdgeLoadingEnabled = true;
      this.load('next');
    }
  }

  load(direction: 'prev' | 'next') {
    if (direction === 'prev' && !this.canScrollToPast) {
      this.finishMobileEdgeLoad();
      return;
    }

    if (environment.mobile && this.isLoadingMoreMobile) {
      return;
    }

    if (environment.mobile) {
      this.isLoadingMoreMobile = true;
      this.pendingMobileSlideDirection = direction;
      const hasPrevSkeleton = this.canScrollToPast ? 1 : 0;
      const currentDatesCount = this.datesGrid.length;
      this.pendingMobileAnchorIndex = direction === 'prev'
        ? hasPrevSkeleton
        : hasPrevSkeleton + currentDatesCount;
      if (this.skeletonObserver) {
        this.skeletonObserver.disconnect();
        this.skeletonObserver = null;
      }
    }
    this.isLoad = false;

    const isPrev = direction === 'prev';
    const arrowElement = document.querySelector(isPrev ? '.arrow-right' : '.arrow-left');
    this.mobileLoadingArrowElement = arrowElement;

    if (arrowElement) {
      arrowElement.classList.add('loader');
    }

    setTimeout(() => {
      let dateFrom = new Date(this.startDiapason);
      let dateTo = new Date(this.endDiapason);
      let isAllowLoadPrices = true;

      if (isPrev) {
        const newDateFrom = new Date(this.startDiapason);
        newDateFrom.setDate(dateFrom.getDate() - 7);

        const newStartDiapason = new Date(this.startDiapason);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (newDateFrom < today) {
          newDateFrom.setTime(today.getTime());
        }

        const newDateTo = new Date(newDateFrom);
        newDateTo.setDate(newDateFrom.getDate() + 6);
        newDateTo.setHours(0, 0, 0, 0);

        this.dateFrom = this.hotelTourCalendarDatesService.getFormattedDateString(newDateFrom);
        this.dateTo = this.hotelTourCalendarDatesService.getFormattedDateString(newDateTo);

        if (newDateFrom < today) {
          isAllowLoadPrices = false;
        } else {
          if (newStartDiapason > newDateFrom) {
            this.startDiapason = this.dateFrom;
          }
        }
        this.bodyParams.params.dateFrom = this.dateFrom;
        this.bodyParams.params.dateTo = this.dateTo;
      } else {
        const newDateTo = new Date(this.endDiapason);
        newDateTo.setDate(dateTo.getDate() + 7);

        const newDateFrom = new Date(newDateTo);
        newDateFrom.setDate(newDateTo.getDate() - 6);

        const newEndDiapason = new Date(this.endDiapason);

        this.dateFrom = this.hotelTourCalendarDatesService.getFormattedDateString(newDateFrom);
        this.dateTo = this.hotelTourCalendarDatesService.getFormattedDateString(newDateTo);

        if (newEndDiapason < newDateTo) {
          this.endDiapason = this.dateTo;
        }

        this.bodyParams.params.dateFrom = this.dateFrom;
        this.bodyParams.params.dateTo = this.dateTo;
      }

      if (isAllowLoadPrices) {
        this.getHotelPriceCalendar(isPrev);
      } else if (environment.mobile) {
        this.finishMobileEdgeLoad();
      }
      this.cdRef.detectChanges();

      if (!environment.mobile) {
        if (arrowElement) {
          arrowElement.classList.remove('loader');
        }
        this.swiperSlide(isPrev);
        this.isLoad = true;
      }

    }, 2000);

    const eventParams = {
      place: 'manager-offer',
    };
  }

  isActiveCell(value: number, nights: number, date: string): boolean {
    // Преобразуем строку в объект Date
    const dateFrom = new Date(this.bodyParams.params.dateFrom);

    // Добавляем 1 день
    dateFrom.setDate(dateFrom.getDate());

    // Преобразуем обратно в строку для сравнения (формат YYYY-MM-DD)
    const dateFromString = dateFrom.toISOString().split('T')[0];

    // nightsFrom — просто число, поэтому прибавляем 1
    const nightsFrom = this.bodyParams.params.nightsFrom;

    // Проверяем, активен ли текущий элемент
    const isMatchingDateAndNights = dateFromString === date && nightsFrom === nights;

    if (this.activeCells.length) {
      return !!value && this.activeCells.some(cell => cell.nights === nights && cell.date === date);
    }

    return !!value && isMatchingDateAndNights;
  }

  private applyPriceCalendarLinerSelection(selection: { date: string; nights: number } | null): void {
    if (!selection) {
      this.selectedLinerCell = null;
      this.pendingLinerSelectionScroll = false;
      return;
    }

    this.selectedLinerCell = selection;
    this.activeCells = [{
      date: selection.date,
      nights: selection.nights,
    }];
    this.pendingLinerSelectionScroll = true;
    this.scrollToPendingSelection();
    this.cdRef.detectChanges();
  }

  private applyPriceCalendarAppliedSelection(selection: { date: string; nights: number } | null): void {
    if (!environment.mobile || !selection || this.selectedLinerCell) {
      return;
    }

    this.activeCells = [{
      date: selection.date,
      nights: selection.nights,
    }];
    this.pendingAppliedDateScroll = this.normalizeDate(selection.date);
    this.scrollToPendingSelection();
    this.cdRef.detectChanges();
  }

  private syncLinerSelectionWithShortSearchParams(selection: { date: string; nights: number }): void {
    const dayCount = selection.nights + 1;

    this.eventBusService.shortSearchParams$.next({
      nightsFrom: selection.nights,
      nightsTo: selection.nights,
      dateFrom: selection.date,
      dateTo: selection.date,
      daysFrom: dayCount,
      daysTo: dayCount,
      forceDates: 1,
    });
  }

  private syncActiveCellWithShortSearchParams(): void {
    const activeCell = this.activeCells[0];

    if (!environment.mobile || !activeCell) {
      return;
    }

    this.syncLinerSelectionWithShortSearchParams(activeCell);
  }

  private prepareAppliedDateScroll(): void {
    if (!environment.mobile || !this.dateFrom) {
      return;
    }

    this.pendingAppliedDateScroll = this.normalizeDate(this.dateFrom);
  }

  private scrollToPendingSelection(): void {
    if (this.scrollToPendingLinerSelection()) {
      return;
    }

    this.scrollToPendingAppliedDate();
  }

  private scrollToPendingLinerSelection(): boolean {
    if (!environment.mobile || !this.pendingLinerSelectionScroll || !this.selectedLinerCell || !this.datesGrid?.length) {
      return false;
    }

    const isScrolled = this.scrollToDate(this.selectedLinerCell.date);

    if (isScrolled) {
      this.pendingLinerSelectionScroll = false;
    }

    return isScrolled;
  }

  private scrollToPendingAppliedDate(): boolean {
    if (!environment.mobile || !this.pendingAppliedDateScroll || !this.datesGrid?.length) {
      return false;
    }

    const isScrolled = this.scrollToDate(this.pendingAppliedDateScroll);

    if (isScrolled) {
      this.pendingAppliedDateScroll = null;
    }

    return isScrolled;
  }

  private scrollToDate(date: string): boolean {
    if (!this.datesGrid?.length) {
      return false;
    }

    const normalizedDate = this.normalizeDate(date);
    const dateIndex = this.datesGrid.findIndex((gridDate) => gridDate.date === normalizedDate);

    if (dateIndex < 0) {
      return false;
    }

    const slideIndex = dateIndex + (this.canScrollToPast ? 1 : 0);

    if (this.swiper) {
      this.swiper.slideTo(slideIndex, 300);
    } else {
      this.pendingMobileAnchorIndex = slideIndex;
    }

    return true;
  }

  private normalizeDate(value: string): string {
    if (!value) {
      return '';
    }

    return String(value).slice(0, 10);
  }

  handleClick(value: number, nights: number, date: string, event: Event): void {
    if (!value) return; // Если нет цены, выходим

    this.resetActiveClasses();

    const target = event.target as HTMLElement;
    const swiperSlide = target.closest('.swiper-slide');
    const nightsColumn = document.querySelector(`.price-calendar .nights-column .column__cell[data-value="${nights}"]`);
    const dateCells = swiperSlide?.querySelectorAll('.column__cell-date');

    const cellIndex = this.activeCells.findIndex(cell => cell.nights === nights && cell.date === date);

    if (cellIndex > -1) {
      // Если уже активен, удалить из массива
      this.activeCells.splice(cellIndex, 1);
    } else {
      // Добавить в массив активных
      this.activeCells.push({nights, date});
    }

    // Переключить класс `.active`
    if (target.classList.contains('active')) {
      target.classList.remove('active');
    } else {
      target.classList.add('active');
    }

    // Добавить класс `.active` для `.nights-column .column__cell`
    nightsColumn?.classList.toggle('active');

    // Добавить классы `.active` для всех `.column__cell-date` внутри текущего `.swiper-slide`
    dateCells?.forEach(cell => cell.classList.toggle('active'));

    if (environment.mobile && !this.isOnlyCalendar) {
      this.eventBusService.priceCalendarLinerSelection$.next(null);
      this.eventBusService.resetPriceCalendarLinerSelection$.next(true);
    }

    const eventParams = {
      date: date,
      days: nights,
      isBest: target.classList.contains('isBest'),
      place: 'tour-list',
    };

    if (this.isOnlyCalendar) {
      eventParams.place = 'manager-offer';

      this.windowService.nativeWindow.open(this.toursOnHotelLink(date, nights));
    } else {
      const dayCount = nights + 1;

      if (!environment.mobile) {
        this.eventBusService.shortSearchParams$.next({
          nightsFrom: nights,
          nightsTo: nights,
          dateFrom: date,
          dateTo: date,
          daysFrom: dayCount,
          daysTo: dayCount,
          forceDates: 1,
        });
      }

      if (!environment.mobile) {
        const currentParams = this.searchService.getLastSearchParams();
        const newParams: HotelToursSearchParams = Object.assign({}, currentParams, {
          nightsFrom: nights,
          nightsTo: nights,
          dateFrom: date,
          dateTo: date,
          daysFrom: dayCount,
          daysTo: dayCount,
          forceDates: 1,
        });
        this.searchService.startNewSearchByHotelId(newParams, 'calendar');
      }
    }

    this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_on_tour_selection_in_calendar', eventParams);
  }

  private resetActiveClasses(): void {
    const activeElements = document.querySelectorAll('.column__cell.active, .column__cell-date.active');
    activeElements.forEach(el => {
      this.renderer.removeClass(el, 'active');
    });

    // Очищаем массив активных ячеек
    this.activeCells = [];
  }

  public get isOnlyCalendar() {
    return this.hotelToursPageConfig.mode === MODE.MODE_PRICE_CALENDAR;
  }

  private toursOnHotelLink(date, nights) {
    const countryAlias = this.hotelToursPageConfig.hotel.countryAlias;
    const regionAlias = this.hotelToursPageConfig.hotel.regionAlias;
    const hotelAlias = this.hotelToursPageConfig.hotel.hotelAlias;
    const params = this.searchService.getLastSearchParams();
    const base = this.windowService.nativeWindow.location.origin;

    params.dateFrom = date;
    params.dateTo = date;
    params.daysFrom = nights + 1;
    params.daysTo = nights + 1;

    const url = new URL(`/new/hotels/${countryAlias}/${regionAlias}/${hotelAlias}/tours`, base);

    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    url.searchParams.set('delta', '0');
    url.searchParams.set('from', 'manager-offer');

    return url;
  }

  private checkCanScrollToPast() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDate = this.datesGrid?.length ? new Date(this.datesGrid[0].date) : new Date(this.startDiapason);
    const dateFrom = firstDate;
    dateFrom.setHours(0, 0, 0, 0);

    this.canScrollToPast = dateFrom > today;
  }

  private swiperSlide(isPrev) {
    this.checkCanScrollToPast();
    let newSlideToIndex = isPrev ? 1 : this.swiper.slides.length - 1;
    if (!this.canScrollToPast && isPrev) {
      newSlideToIndex = 0;
    }
    this.swiper.slideTo(newSlideToIndex, 500);
  }

  private finishMobileEdgeLoad() {
    this.mobileLoadingArrowElement?.classList.remove('loader');
    this.mobileLoadingArrowElement = null;
    this.isLoadingMoreMobile = false;
    this.pendingMobileAnchorIndex = null;
    this.pendingMobileSlideDirection = null;
    this.isLoad = true;
  }
}
